import { Stack } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
} from '@retail/app/stores/selected-context';
import {
  NewFunctionalityChip,
  PageLayout,
  SuspenseWithSentryErrorBoundary,
  TabContent,
} from '@retail/components';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { Tabs } from '@shared/components';
import { useMemo } from 'react';
import { Navigate, Outlet, useMatch } from 'react-router';

interface Tabs {
  label: string | React.ReactNode;
  value: AdministrationTab;
  to: string;
}
interface Props {
  administrationRoute: typeof mgPrisRoutes.administration;
}

type AdministrationTab = 'suppliers' | 'rounding' | 'assortments' | 'fixedPrice';

export function AdministrationPage({ administrationRoute }: Props) {
  const t = useAppTFunction();

  const { userCanRead, userCanCreate } = usePermissionsForCurrentUser();
  const isPriceRoundingEnabled = useIsFeatureEnabled('price-rounding');
  const localPriceSuggestionIsEnabled = useIsFeatureEnabled('local-price-suggestions');

  const showRoundingTab = useMemo(
    () => isPriceRoundingEnabled && userCanRead('ROUNDING'),
    [isPriceRoundingEnabled, userCanRead]
  );
  const showAssortmentsTab = useMemo(() => userCanCreate('SUB_ASSORTMENTS'), [userCanCreate]);
  const showLocalPriceSuggestionsTab = useMemo(
    () => userCanRead('PRICE_SUGGESTIONS') && localPriceSuggestionIsEnabled,
    [localPriceSuggestionIsEnabled, userCanRead]
  );

  const administrationRouteMatch = useMatch(`${administrationRoute.fullRoutePath}`);
  const roundingRouteMatch = useMatch(`${administrationRoute.rounding.fullRoutePath}`);
  const assortmentsRouteMatch = useMatch(`${administrationRoute.assortments.fullRoutePath}`);
  const fixedPriceRouteMatch = useMatch(`${administrationRoute.fixedPrice.fullRoutePath}`);

  const tabs: Tabs[] = useMemo(() => {
    const result: Tabs[] = [
      {
        label: t('common.suppliers'),
        value: 'suppliers',
        to: administrationRoute.suppliers.getFullLinkPath(),
      },
    ];
    if (showAssortmentsTab) {
      result.push({
        label: t('myAssortment.columns.subAssortments'),
        value: 'assortments',
        to: administrationRoute.assortments.getFullLinkPath(),
      });
    }
    if (showRoundingTab) {
      result.push({
        label: (
          <Stack flexDirection="row" alignItems="flex-start" gap={0.5}>
            {t('rounding.tabTitle')}
            <NewFunctionalityChip />
          </Stack>
        ),
        value: 'rounding',
        to: administrationRoute.rounding.getFullLinkPath(),
      });
    }
    if (showLocalPriceSuggestionsTab) {
      result.push({
        label: t('calculus.localPriceSuggestions'),
        value: 'fixedPrice',
        to: administrationRoute.fixedPrice.getFullLinkPath(),
      });
    }
    return result;
  }, [administrationRoute, showAssortmentsTab, showLocalPriceSuggestionsTab, showRoundingTab, t]);

  const currentTab: AdministrationTab = useMemo(() => {
    if (roundingRouteMatch) return 'rounding';
    else if (assortmentsRouteMatch) return 'assortments';
    else if (fixedPriceRouteMatch) return 'fixedPrice';
    return 'suppliers';
  }, [assortmentsRouteMatch, fixedPriceRouteMatch, roundingRouteMatch]);

  if (administrationRouteMatch)
    return <Navigate to={administrationRoute.suppliers.getFullLinkPath()} replace />;

  return (
    <PageLayout maxWidth="full" fixedHeight>
      <Tabs<AdministrationTab> prefixId="administration" tabs={tabs} currentTab={currentTab} />

      <TabContent sx={{ overflow: 'hidden', pb: 1, p: 0, flexGrow: 1 }}>
        <SuspenseWithSentryErrorBoundary>
          <Outlet />
        </SuspenseWithSentryErrorBoundary>
      </TabContent>
    </PageLayout>
  );
}
